import { showToastMessage } from './commonFunctions';
import { LockApiError423Data, LockApiError409Data } from '../types/validationLockTypes';

export interface ParsedLockError {
  isLockError: boolean;
  status?: number;
  message: string;
  type?: '423_LOCKED' | '409_CONFLICT' | '403_FORBIDDEN' | '400_INVALID_SESSION' | '404_NOT_FOUND';
  data423?: LockApiError423Data;
  data409?: LockApiError409Data;
}

/**
 * Parses API error responses to detect lock-related statuses (423, 409, 403, 400).
 * Displays a clean, descriptive toast and returns structured error data.
 */
export const handleLockApiError = (error: any, fallbackMessage?: string): ParsedLockError => {
  const status = error?.response?.status || error?.status;
  const resData = error?.response?.data || error?.data || {};
  const serverMsg = resData?.message || error?.message || '';

  // 1. 423 Locked
  if (status === 423) {
    const data423 = resData?.data as LockApiError423Data | undefined;
    
    // Check if it's a satellite group delete breakdown
    if (data423 && (data423.testPlans || data423.testCases || data423.validationConfigs || data423.setups || data423.validationSessions)) {
      const parts: string[] = [];
      if (data423.testPlans) parts.push(`${data423.testPlans} Test Plan(s)`);
      if (data423.testCases) parts.push(`${data423.testCases} Test Case(s)`);
      if (data423.validationConfigs) parts.push(`${data423.validationConfigs} Config(s)`);
      if (data423.setups) parts.push(`${data423.setups} Setup(s)`);
      if (data423.validationSessions) parts.push(`${data423.validationSessions} Session(s)`);
      
      const breakdownMsg = `Cannot delete: contains locked items (${parts.join(', ')}). Unlock them first.`;
      showToastMessage('error', breakdownMsg);
      return { isLockError: true, status: 423, message: breakdownMsg, type: '423_LOCKED', data423 };
    }

    // Single or bulk locked items message
    const msg = serverMsg || fallbackMessage || 'This item is locked. Contact an administrator to unlock it.';
    showToastMessage('error', msg);
    return { isLockError: true, status: 423, message: msg, type: '423_LOCKED', data423 };
  }

  // 2. 409 Conflict (Unlock test case blocked by test plan)
  if (status === 409) {
    const data409 = resData?.data as LockApiError409Data | undefined;
    const blocked = data409?.blocked || [];
    
    let conflictMsg = serverMsg || fallbackMessage || 'Cannot unlock test case: it is referenced by a locked Test Plan.';
    if (blocked.length > 0) {
      const firstBlocked = blocked[0];
      const planName = firstBlocked.lockedPlanNames?.[0] || firstBlocked.lockedPlanIds?.[0] || 'a locked Test Plan';
      conflictMsg = `Cannot unlock "${firstBlocked.name || 'test case'}": it belongs to locked Test Plan "${planName}". Please unlock the plan first.`;
    }

    showToastMessage('error', conflictMsg);
    return { isLockError: true, status: 409, message: conflictMsg, type: '409_CONFLICT', data409 };
  }

  // 3. 403 Forbidden (Non-admin attempting lock action)
  if (status === 403) {
    const msg = serverMsg || 'Only a super_admin or a tenant_admin of the current tenant can lock or unlock';
    showToastMessage('error', msg);
    return { isLockError: true, status: 403, message: msg, type: '403_FORBIDDEN' };
  }

  // 4. 400 Bad Request on Session lock (not a live validation session)
  if (status === 400 && resData?.data?.notValidationSessions) {
    const msg = serverMsg || 'Only live validation sessions can be locked.';
    showToastMessage('error', msg);
    return { isLockError: true, status: 400, message: msg, type: '400_INVALID_SESSION' };
  }

  // 5. 404 Not Found (Missing IDs)
  if (status === 404 && resData?.data?.missingIds) {
    const msg = serverMsg || 'One or more requested items no longer exist. Please refresh the page.';
    showToastMessage('error', msg);
    return { isLockError: true, status: 404, message: msg, type: '404_NOT_FOUND' };
  }

  if (fallbackMessage) {
    showToastMessage('error', fallbackMessage);
  }

  return { isLockError: false, status, message: fallbackMessage || serverMsg };
};
